import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { useIntl, type MessageDescriptor } from "react-intl"
import {
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  FileText,
  GraduationCap,
  Home,
  Layers,
  ListChecks,
  LogOut,
  Menu,
  LayoutDashboard,
  MessageSquareWarning,
  Settings,
  Shield,
  User,
  X,
  Languages,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useFeedback, useRegisterNavTrigger } from "@/components/feedback/FeedbackProvider"
import { BetaBadge } from "@/components/BetaBadge"
import { Badge } from "@/components/ui/badge"
import { ThemeToggle } from "@/components/ui/theme-toggle"
import { useChangeLocale } from "@/components/LanguageSwitcher"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

const STORAGE_KEY = "main-nav-open"
const BRAND = "Quiziqal"

export type NavItemDef = {
  href: string
  icon: React.ComponentType<{ className?: string }>
  /** Plain text (the admin nav stays in English) or a message id. */
  label: string | MessageDescriptor
  match: (url: string) => boolean
  /** Hidden from instructors, who only manage official content. */
  adminOnly?: boolean
  /** Hidden until the user has unlocked a program. */
  programOnly?: boolean
  /** A shared page prop holding a count to show beside the label. */
  badgeProp?: "feedback_unreviewed_count"
}

const DEFAULT_NAV_ITEMS: NavItemDef[] = [
  {
    href: "/assistant",
    icon: Home,
    label: { id: "nav.home" },
    match: (url) => url === "/" || url.startsWith("/assistant") || url.startsWith("/conversations"),
  },
  {
    href: "/dashboard",
    icon: LayoutDashboard,
    label: { id: "nav.dashboard" },
    match: (url) => url.startsWith("/dashboard"),
  },
  {
    href: "/flashcards",
    icon: Layers,
    label: { id: "nav.flashcards" },
    match: (url) => url.startsWith("/flashcards") || url.startsWith("/decks") || url.startsWith("/review"),
  },
  {
    href: "/quizzes",
    icon: ListChecks,
    label: { id: "nav.quizzes" },
    match: (url) => url.startsWith("/quizzes") || url.startsWith("/attempts"),
  },
  {
    href: "/practice_exams",
    icon: ClipboardList,
    label: { id: "nav.practice_exams" },
    programOnly: true,
    match: (url) => url.startsWith("/practice_exams") || url.startsWith("/exam_attempts"),
  },
  {
    href: "/sources",
    icon: FileText,
    label: { id: "nav.sources" },
    match: (url) => url.startsWith("/sources") || url.startsWith("/generations"),
  },
]

function useMainNavOpen() {
  const [open, setOpen] = React.useState<boolean>(true)
  React.useEffect(() => {
    if (typeof window === "undefined") return
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored !== null) setOpen(stored === "true")
  }, [])
  React.useEffect(() => {
    if (typeof window === "undefined") return
    window.localStorage.setItem(STORAGE_KEY, String(open))
  }, [open])
  return [open, setOpen] as const
}

export function MainNav({
  items = DEFAULT_NAV_ITEMS,
  brandHref = "/assistant",
  showAdminLink = true,
}: {
  items?: NavItemDef[]
  brandHref?: string
  /** Show the admins-only "Admin" link. The admin area's own nav already lists these pages. */
  showAdminLink?: boolean
} = {}) {
  const intl = useIntl()
  const [open, setOpen] = useMainNavOpen()
  const [mobileOpen, setMobileOpen] = React.useState(false)

  return (
    <>
      <aside
        className={cn(
          "sticky top-0 hidden h-screen shrink-0 flex-col bg-canvas transition-[width] duration-200 lg:flex",
          open ? "w-56" : "w-14",
        )}
      >
        <RailBody
          open={open}
          onToggle={() => setOpen(!open)}
          items={items}
          brandHref={brandHref}
          showAdminLink={showAdminLink}
        />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-ink-display/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-page shadow-xl">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-hairline px-4">
              <span className="flex items-center gap-2">
                <span className="font-display text-sm font-semibold text-ink-display">{BRAND}</span>
                <BetaBadge feedback />
              </span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink-display"
                aria-label={intl.formatMessage({ id: "nav.close" })}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <RailNav open items={items} showAdminLink={showAdminLink} onClose={() => setMobileOpen(false)} />
            <div className="border-t border-hairline p-2">
              <UserMenu open />
            </div>
          </aside>
        </div>
      )}

      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="fixed right-3 top-3 z-30 inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-xl border border-hairline bg-page text-ink-body hover:bg-surface lg:hidden"
        aria-label={intl.formatMessage({ id: "nav.open" })}
      >
        <Menu className="h-4 w-4" />
      </button>
    </>
  )
}

function RailBody({
  open,
  onToggle,
  items,
  brandHref,
  showAdminLink,
}: {
  open: boolean
  onToggle: () => void
  items: NavItemDef[]
  brandHref: string
  showAdminLink: boolean
}) {
  const intl = useIntl()
  return (
    <>
      <div
        className={cn(
          "flex h-14 shrink-0 items-center gap-3 border-b border-hairline px-3",
          open ? "justify-between" : "justify-center",
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Link
            href={brandHref}
            className="flex min-w-0 items-center gap-2 text-ink-display no-underline"
            aria-label={BRAND}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-faded font-display text-sm font-semibold text-accent">
              {BRAND.charAt(0)}
            </span>
            {open && (
              <span className="truncate font-display text-sm font-semibold">
                {BRAND}
              </span>
            )}
          </Link>
          {open && <BetaBadge feedback />}
        </div>
        {open && (
          <button
            type="button"
            onClick={onToggle}
            className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink-display"
            aria-label={intl.formatMessage({ id: "nav.collapse" })}
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      <RailNav open={open} items={items} showAdminLink={showAdminLink} />

      {!open && (
        <div className="border-t border-hairline p-2">
          <button
            type="button"
            onClick={onToggle}
            className="flex h-9 w-full cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink-display"
            aria-label={intl.formatMessage({ id: "nav.expand" })}
          >
            <ChevronsRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="border-t border-hairline p-2">
        <UserMenu open={open} />
      </div>
    </>
  )
}

function RailNav({
  open,
  items,
  showAdminLink,
  onClose,
}: {
  open: boolean
  items: NavItemDef[]
  showAdminLink: boolean
  onClose?: () => void
}) {
  const { url, props } = usePage<PageProps>()
  const isAdmin = props.current_user?.admin ?? false
  const isInstructor = props.current_user?.instructor ?? false
  const hasProgram = props.current_user?.program_access ?? false
  return (
    <nav
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-1 p-2 text-sm",
        open ? "overflow-x-hidden overflow-y-auto" : "overflow-visible",
      )}
    >
      {items
        .filter((item) => (!item.adminOnly || isAdmin) && (!item.programOnly || hasProgram))
        .map((item) => (
          <NavItem
            key={item.href}
            href={item.href}
            icon={item.icon}
            label={item.label}
            active={item.match(url)}
            open={open}
            badge={item.badgeProp ? props[item.badgeProp] : undefined}
            onClick={onClose}
          />
        ))}
      {showAdminLink && (isAdmin || isInstructor) && (
        <AdminNavLink open={open} url={url} admin={isAdmin} onClick={onClose} />
      )}
      <FeedbackNavItem open={open} onClick={onClose} />
    </nav>
  )
}

/**
 * Link to the admin area. Admins land on feedback (with the count nobody has
 * reviewed yet); instructors, who only manage official content, land on decks.
 */
function AdminNavLink({
  open,
  url,
  admin,
  onClick,
}: {
  open: boolean
  url: string
  admin: boolean
  onClick?: () => void
}) {
  const { props } = usePage<PageProps>()
  return (
    <NavItem
      href={admin ? "/admin/feedbacks" : "/admin/decks"}
      icon={admin ? Shield : GraduationCap}
      label={admin ? { id: "nav.admin" } : { id: "nav.instructor" }}
      active={url.startsWith("/admin")}
      open={open}
      badge={admin ? props.feedback_unreviewed_count : undefined}
      onClick={onClick}
    />
  )
}

/** The "Send feedback" entry at the bottom of the nav; opens the global feedback dialog. */
function FeedbackNavItem({ open, onClick }: { open: boolean; onClick?: () => void }) {
  const { setOpen } = useFeedback()
  useRegisterNavTrigger()
  return (
    <div className="mt-auto">
      <NavItem
        icon={MessageSquareWarning}
        label={{ id: "nav.send_feedback" }}
        open={open}
        onClick={() => {
          onClick?.()
          setOpen(true)
        }}
      />
    </div>
  )
}

function NavItem({
  href,
  icon: Icon,
  label: labelProp,
  active,
  open,
  onClick,
  badge,
}: {
  href?: string
  icon: React.ComponentType<{ className?: string }>
  label: string | MessageDescriptor
  active?: boolean
  open: boolean
  onClick?: () => void
  /** A count shown beside the label (a dot when the rail is collapsed); hidden when zero. */
  badge?: number | null
}) {
  const intl = useIntl()
  const label = typeof labelProp === "string" ? labelProp : intl.formatMessage(labelProp)
  const toReview = (n: number) => intl.formatMessage({ id: "nav.to_review" }, { count: n })
  const className = cn(
    "relative flex items-center gap-3 rounded-lg text-sm/5 font-medium no-underline",
    open ? "px-2 py-2" : "mx-auto h-9 w-9 justify-center",
    active
      ? "bg-ink-display/5 text-ink-display"
      : "text-ink-body hover:bg-ink-display/5 hover:text-ink-display",
  )
  const content = (
    <>
      {active && <span aria-hidden="true" className="absolute inset-y-2 -left-2 w-0.5 rounded-full bg-ink-display" />}
      <Icon className={cn("h-4 w-4 shrink-0", active ? "text-ink-display" : "text-ink-muted")} />
      {open && <span className="truncate">{label}</span>}
      {!!badge && open && (
        <span className="ml-auto" aria-label={toReview(badge)}>
          <Badge tone="signal">{badge}</Badge>
        </span>
      )}
      {!!badge && !open && (
        <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-signal" aria-label={toReview(badge)} />
      )}
    </>
  )

  return (
    <div className="group/nav-item relative">
      {href ? (
        <Link href={href} onClick={onClick} aria-label={open ? undefined : label} className={className}>
          {content}
        </Link>
      ) : (
        <button
          type="button"
          onClick={onClick}
          aria-label={open ? undefined : label}
          className={cn(className, "cursor-pointer", open && "w-full text-left")}
        >
          {content}
        </button>
      )}
      {!open && (
        <span
          role="tooltip"
          className="pointer-events-none absolute left-full top-1/2 z-50 ml-[13px] -translate-y-1/2 whitespace-nowrap rounded-xl border border-hairline bg-page px-2 py-1 text-xs font-medium text-ink-display opacity-0 shadow-sm transition-opacity group-hover/nav-item:opacity-100"
        >
          {label}
        </span>
      )}
    </div>
  )
}

/** Language submenu in the account menu. Each language is named in itself. */
function LanguageMenu() {
  const intl = useIntl()
  const { locale, available_locales } = usePage<PageProps>().props
  const change = useChangeLocale()
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Languages /> {intl.formatMessage({ id: "language.label" })}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuRadioGroup value={locale} onValueChange={change}>
          {available_locales.map((option) => (
            <DropdownMenuRadioItem key={option.code} value={option.code} lang={option.code}>
              {option.name}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}

function UserMenu({ open }: { open: boolean }) {
  const intl = useIntl()
  const { props } = usePage<PageProps>()
  const email = props.current_user?.email ?? ""
  // The saved name (Profile page), else the email.
  const displayName = props.current_user?.name?.trim() || email
  const isAdmin = props.current_user?.admin ?? false
  const isInstructor = props.current_user?.instructor ?? false
  const isTranslator = props.current_user?.translator ?? false
  const initial = displayName.charAt(0).toUpperCase() || "?"

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={open ? undefined : displayName || intl.formatMessage({ id: "nav.account" })}
          className={cn(
            "group/user relative flex w-full cursor-pointer items-center gap-3 rounded-lg text-left text-ink-body hover:bg-ink-display/5 hover:text-ink-display focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            open ? "px-2 py-2" : "h-10 justify-center",
          )}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-faded text-xs font-semibold text-accent">
            {initial}
          </span>
          {open ? (
            <span className="min-w-0 flex-1 truncate text-sm">{displayName}</span>
          ) : (
            <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-xl border border-hairline bg-page px-2 py-1 text-xs font-medium text-ink-display opacity-0 shadow-md transition-opacity group-hover/user:opacity-100">
              {displayName}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56">
        <DropdownMenuLabel className="normal-case tracking-normal">
          <span className="block text-[10px] uppercase tracking-wider text-ink-muted">
            {intl.formatMessage({ id: "nav.signed_in_as" })}
          </span>
          <span className="block truncate text-xs font-medium text-ink-display">
            {email}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/profile" className="no-underline">
            <User /> {intl.formatMessage({ id: "nav.profile" })}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/settings" className="no-underline">
            <Settings /> {intl.formatMessage({ id: "nav.settings" })}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-2">
          <ThemeToggle block />
        </div>
        <DropdownMenuSeparator />
        <LanguageMenu />
        {isTranslator && (
          <DropdownMenuItem asChild>
            <Link href="/admin/translations" className="no-underline">
              <Languages /> {intl.formatMessage({ id: "nav.translations" })}
            </Link>
          </DropdownMenuItem>
        )}
        {(isAdmin || isInstructor) && (
          <DropdownMenuItem asChild>
            <Link href={isAdmin ? "/admin/users" : "/admin/decks"} className="no-underline">
              {isAdmin ? <Shield /> : <GraduationCap />} {intl.formatMessage(isAdmin ? { id: "nav.admin" } : { id: "nav.instructor_tools" })}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            router.delete("/logout")
          }}
        >
          <LogOut /> {intl.formatMessage({ id: "nav.sign_out" })}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
